import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Download, PaperPlaneRight, FilePdf, FunnelSimple, Trash, ArrowsCounterClockwise, Prohibit, Toolbox } from '@phosphor-icons/react';
import ArticleSelector from '../components/ArticleSelector';
import VatRateSelect from '../components/VatRateSelect';
import { downloadBlob } from '../utils/download';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [keys, setKeys] = useState([]);
  const [itProducts, setItProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [printing, setPrinting] = useState([]);
  const [businessCards, setBusinessCards] = useState([]);
  const [binding, setBinding] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);
  const [creditNoteInvoice, setCreditNoteInvoice] = useState(null);
  const [creditNoteForm, setCreditNoteForm] = useState({ items: [], reason: '' });
  const [alreadyCredited, setAlreadyCredited] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingInvoice, setEditingInvoice] = useState(null);

  const [invoiceForm, setInvoiceForm] = useState({
    client_id: '',
    items: [],
    notes: '',
    additional_info: '',
    payment_method: 'cash'
  });

  const [itemMode, setItemMode] = useState('product'); // 'product' or 'custom'
  const [selectedProductKey, setSelectedProductKey] = useState(''); // format: "type:id"
  const [currentItem, setCurrentItem] = useState({
    type: '',
    description: '',
    quantity: '1',
    unit_price: '',
    vat_rate: 0.085,
  });

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchKeys();
    fetchItProducts();
    fetchServices();
    fetchTariffs();
    fetchPrestations();
  }, []);

  const fetchInvoices = async (filters = {}) => {
    try {
      let url = `${API_URL}/api/invoices`;
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/keys`, { withCredentials: true });
      setKeys(response.data);
    } catch (error) {
      console.error('Error fetching keys:', error);
    }
  };

  const fetchItProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/it-products`, { withCredentials: true });
      setItProducts(response.data);
    } catch (error) {
      console.error('Error fetching IT products:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services?only_active=true`, { withCredentials: true });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchTariffs = async () => {
    try {
      const [pr, bc, bi] = await Promise.all([
        axios.get(`${API_URL}/api/printing`, { withCredentials: true }),
        axios.get(`${API_URL}/api/business-cards`, { withCredentials: true }),
        axios.get(`${API_URL}/api/binding`, { withCredentials: true }),
      ]);
      setPrinting(pr.data);
      setBusinessCards(bc.data);
      setBinding(bi.data);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
    }
  };

  const fetchPrestations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/prestations`, { withCredentials: true });
      setPrestations(response.data);
    } catch (error) {
      console.error('Error fetching prestations:', error);
    }
  };

  const addItemToInvoice = () => {
    let newItem;
    
    if (itemMode === 'product') {
      if (!selectedProductKey) {
        toast.error('Veuillez sélectionner un produit');
        return;
      }
      
      const [productType, productId] = selectedProductKey.split(':');
      let product;
      if (productType === 'key') product = keys.find(k => k.id === productId);
      else if (productType === 'it_product') product = itProducts.find(p => p.id === productId);
      else if (productType === 'service') product = services.find(s => s.id === productId);
      else if (productType === 'printing') product = printing.find(p => p.id === productId);
      else if (productType === 'business_card') product = businessCards.find(p => p.id === productId);
      else if (productType === 'binding') product = binding.find(p => p.id === productId);
      else if (productType === 'prestation') product = prestations.find(p => p.id === productId);
      
      if (!product) {
        toast.error('Produit introuvable');
        return;
      }
      
      const quantity = parseInt(currentItem.quantity);
      const hasStock = productType === 'key' || productType === 'it_product';
      if (hasStock && quantity > product.stock_quantity) {
        toast.error(`Stock insuffisant. Disponible: ${product.stock_quantity}`);
        return;
      }
      
      // Catalog prices are entered as TTC by convention (see Settings tooltip).
      // Convert to HT so the invoice's "P.U. HT" column is truly HT.
      const vat = product.vat_rate !== undefined ? product.vat_rate : 0.085;
      const toHT = (ttc) => (vat > 0 ? +(ttc / (1 + vat)).toFixed(4) : ttc);

      let unitPrice, typeLabel, descLabel;
      if (productType === 'service') {
        unitPrice = toHT(product.unit_price);
        typeLabel = product.category || 'Service';
        descLabel = product.name + (product.description ? ` - ${product.description}` : '');
      } else if (productType === 'printing') {
        unitPrice = toHT(product.price);
        typeLabel = 'Impression';
        descLabel = `Impression ${product.format} - ${product.paper_type} - ${product.is_color ? 'Couleur' : 'Monochrome'}`;
      } else if (productType === 'business_card') {
        unitPrice = toHT(product.price);
        typeLabel = 'Carte de visite';
        descLabel = `Cartes de visite - Papier ${product.paper_type} - ${product.is_color ? 'Couleur' : 'Monochrome'}`;
      } else if (productType === 'binding') {
        unitPrice = toHT(product.price);
        typeLabel = 'Reliure';
        descLabel = `Reliure ${product.min_pages} à ${product.max_pages} pages`;
      } else if (productType === 'prestation') {
        unitPrice = toHT(product.price);
        typeLabel = 'Prestation';
        descLabel = product.name;
      } else {
        unitPrice = toHT(product.sale_price);
        typeLabel = productType === 'key' ? 'Clé' : 'Produit IT';
        descLabel = product.name;
        if (productType === 'key' && product.characteristics) descLabel += ` - ${product.characteristics}`;
        else if (productType === 'it_product' && product.category) descLabel += ` (${product.category})`;
      }
      
      newItem = {
        type: typeLabel,
        description: descLabel,
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
        product_id: productId,
        product_type: productType,
        vat_rate: product.vat_rate !== undefined ? product.vat_rate : 0.085,
      };
    } else {
      if (!currentItem.description || !currentItem.unit_price) {
        toast.error('Veuillez remplir tous les champs');
        return;
      }
      
      const quantity = parseInt(currentItem.quantity);
      const unitPrice = parseFloat(currentItem.unit_price);
      
      newItem = {
        type: currentItem.type || 'Service',
        description: currentItem.description,
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
        product_id: null,
        product_type: null,
        vat_rate: currentItem.vat_rate !== undefined ? currentItem.vat_rate : 0.085,
      };
    }

    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, newItem]
    });

    setCurrentItem({ type: '', description: '', quantity: '1', unit_price: '', vat_rate: 0.085 });
    setSelectedProductKey('');
  };

  const removeItem = (index) => {
    const newItems = invoiceForm.items.filter((_, i) => i !== index);
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const computeLineTotal = (qty, unit, discountPercent) => {
    const base = (qty || 0) * (unit || 0);
    const pct = Math.max(0, Math.min(100, parseFloat(discountPercent) || 0));
    return Math.max(0, base * (1 - pct / 100));
  };

  const updateItemDiscount = (index, value) => {
    const newItems = invoiceForm.items.map((it, i) => {
      if (i !== index) return it;
      const next = { ...it };
      const pct = Math.max(0, Math.min(100, parseFloat(value) || 0));
      next.discount_percent = pct;
      // Keep legacy fields cleared to avoid confusion in DB reads
      next.discount_type = pct > 0 ? 'percent' : null;
      next.discount_value = pct;
      next.total_price = computeLineTotal(next.quantity, next.unit_price, pct);
      return next;
    });
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };


  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    
    if (invoiceForm.items.length === 0) {
      toast.error('Ajoutez au moins un article à la facture');
      return;
    }

    try {
      if (editingInvoice) {
        await axios.put(`${API_URL}/api/invoices/${editingInvoice.id}`, invoiceForm, { withCredentials: true });
        toast.success('Facture modifiée - Stock mis à jour');
      } else {
        await axios.post(`${API_URL}/api/invoices`, invoiceForm, { withCredentials: true });
        toast.success('Facture créée - Stock mis à jour');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchInvoices();
      fetchKeys();
      fetchItProducts();
    } catch (error) {
      console.error('Error saving invoice:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la sauvegarde');
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!(await confirm({
      title: 'Annuler cette facture ?',
      description: 'Un avoir sera automatiquement émis pour le montant restant. Le stock sera restauré et un mouvement de caisse négatif sera créé si une session est ouverte. Cette action est irréversible.',
      confirmText: 'Annuler la facture',
      cancelText: 'Retour',
    }))) return;
    try {
      const r = await axios.post(`${API_URL}/api/invoices/${invoiceId}/cancel`, {}, { withCredentials: true });
      if (r.data.credit_note_number) {
        toast.success(`Facture annulée - Avoir ${r.data.credit_note_number} créé`);
      } else {
        toast.success(r.data.message || 'Facture annulée');
      }
      fetchInvoices();
      fetchKeys();
      fetchItProducts();
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const resetForm = () => {
    setInvoiceForm({ client_id: '', items: [], notes: '', additional_info: '', payment_method: 'cash' });
    setCurrentItem({ type: '', description: '', quantity: '1', unit_price: '', vat_rate: 0.085 });
    setSelectedProductKey('');
    setItemMode('product');
    setEditingInvoice(null);
  };

  const downloadPDF = async (invoiceId) => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${invoiceId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      downloadBlob(response.data, `facture_${invoiceId}.pdf`, 'application/pdf');
      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const downloadExcel = async () => {
    try {
      let url = `${API_URL}/api/invoices/export/pdf`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });
      const periodSuffix = startDate && endDate ? `_${startDate}_au_${endDate}` : '';
      downloadBlob(response.data, `factures${periodSuffix}.pdf`, 'application/pdf');
      toast.success('Export PDF téléchargé');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const openSendDialog = (invoice) => {
    setSelectedInvoice(invoice);
    const client = clients.find(c => c.id === invoice.client_id);
    setRecipientEmail(client?.email || '');
    setShowSendDialog(true);
  };

  const openCreditNoteDialog = async (invoice) => {
    setCreditNoteInvoice(invoice);
    setCreditNoteForm({
      items: invoice.items.map((it, idx) => ({ item_index: idx, quantity: 0, selected: false })),
      reason: '',
    });
    try {
      const r = await axios.get(`${API_URL}/api/credit-notes?invoice_id=${invoice.id}`, { withCredentials: true });
      const credited = {};
      r.data.forEach(cn => {
        cn.items.forEach(ci => {
          if (typeof ci.source_item_index === 'number') {
            credited[ci.source_item_index] = (credited[ci.source_item_index] || 0) + ci.quantity;
          }
        });
      });
      setAlreadyCredited(credited);
    } catch (e) {
      setAlreadyCredited({});
    }
    setShowCreditNoteDialog(true);
  };

  const submitCreditNote = async () => {
    const items = creditNoteForm.items
      .filter(i => i.selected && i.quantity > 0)
      .map(i => ({ item_index: i.item_index, quantity: parseFloat(i.quantity) }));
    if (items.length === 0) {
      toast.error('Sélectionnez au moins un article à créditer');
      return;
    }
    try {
      const r = await axios.post(
        `${API_URL}/api/invoices/${creditNoteInvoice.id}/credit-notes`,
        { items, reason: creditNoteForm.reason },
        { withCredentials: true }
      );
      toast.success(`Avoir ${r.data.credit_note_number} créé`);
      setShowCreditNoteDialog(false);
      setCreditNoteInvoice(null);
      fetchInvoices();
      fetchKeys();
      fetchItProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création de l\'avoir');
    }
  };

  const creditTotalAmount = creditNoteInvoice
    ? creditNoteForm.items.reduce((sum, ci) => {
        if (!ci.selected || !ci.quantity) return sum;
        const orig = creditNoteInvoice.items[ci.item_index];
        return sum + (parseFloat(ci.quantity) || 0) * orig.unit_price;
      }, 0)
    : 0;

  const sendEmail = async () => {
    if (!recipientEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/invoices/${selectedInvoice.id}/send?recipient_email=${recipientEmail}`,
        {},
        { withCredentials: true }
      );
      toast.success('Email envoyé');
      setShowSendDialog(false);
    } catch (error) {
      console.error('Error sending email:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.msg || `Erreur ${error.response?.status || ''} — vérifiez la configuration Resend (domaine vérifié ?).`);
      toast.error(msg, { duration: 8000 });
    }
  };

  const applyFilters = () => {
    fetchInvoices({ start_date: startDate, end_date: endDate });
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    fetchInvoices();
  };

  // Canonical totals for the current form (HT / VAT / TTC).
  const formTotals = invoiceForm.items.reduce(
    (acc, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const unit = parseFloat(item.unit_price) || 0;
      const pct = Math.max(0, Math.min(100, parseFloat(item.discount_percent) || 0));
      const vat = parseFloat(item.vat_rate) || 0;
      const lineHT = qty * unit * (1 - pct / 100);
      acc.ht += lineHT;
      acc.vat += lineHT * vat;
      return acc;
    },
    { ht: 0, vat: 0 },
  );
  const totalHT = formTotals.ht;
  const totalVAT = formTotals.vat;
  const totalTTC = totalHT + totalVAT;
  // Default company VAT rate for display label (uses the most common rate in the items list, defaults to 8.5%)
  const displayVatRate = invoiceForm.items.length
    ? (parseFloat(invoiceForm.items[0].vat_rate) || 0.085)
    : 0.085;

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="invoices-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Factures</h1>
            <p className="text-slate-600">Créez et gérez vos factures</p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <Button
                onClick={downloadExcel}
                data-testid="export-pdf-period-btn"
                variant="outline"
                className="border-slate-300"
                title={(startDate || endDate) ? `Période: ${startDate || '—'} → ${endDate || '—'}` : 'Sélectionnez une période dans les filtres ci-dessous'}
              >
                <FilePdf size={20} className="mr-2" />
                Export PDF
              </Button>
            )}
            <Button
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              data-testid="create-invoice-btn"
              className="bg-orange-500 hover:bg-orange-400 text-white"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Nouvelle facture
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Date de début</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Date de fin</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            <Button
              onClick={applyFilters}
              data-testid="apply-filters-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              <FunnelSimple size={20} className="mr-2" />
              Filtrer
            </Button>
            <Button
              onClick={clearFilters}
              data-testid="clear-filters-btn"
              variant="outline"
            >
              Réinitialiser
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par numéro, client ou utilisateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-invoices-input"
                className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Numéro</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Client</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Montant</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Émis par</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices
                    .filter(inv => !searchQuery || 
                      inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      inv.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      inv.issued_by.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((invoice) => {
                    const cancelled = invoice.status === 'cancelled';
                    const hasService = (invoice.items || []).some(it => it.product_type === 'service');
                    return (
                    <tr
                      key={invoice.id}
                      className={`border-b border-slate-100 ${cancelled ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`}
                      data-testid={cancelled ? `invoice-row-cancelled-${invoice.id}` : `invoice-row-${invoice.id}`}
                    >
                      <td className={`py-3 px-4 text-sm font-medium ${cancelled ? 'text-red-700' : 'text-slate-900'}`}>
                        <div className="flex items-center gap-2">
                          {invoice.invoice_number}
                          {cancelled && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white no-underline" data-testid={`badge-cancelled-${invoice.id}`}>
                              ANNULÉE
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`py-3 px-4 text-sm ${cancelled ? 'text-red-700' : 'text-slate-900'}`}>{invoice.client_name}</td>
                      <td className={`py-3 px-4 text-sm ${cancelled ? 'text-red-700' : 'text-slate-900'}`}>{invoice.total_amount.toFixed(2)} €</td>
                      <td className={`py-3 px-4 text-sm ${cancelled ? 'text-red-600' : 'text-slate-600'}`}>
                        {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className={`py-3 px-4 text-sm ${cancelled ? 'text-red-600' : 'text-slate-600'}`}>{invoice.issued_by}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => downloadPDF(invoice.id)}
                          data-testid={`download-pdf-${invoice.id}`}
                          className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md mr-1"
                          title="Télécharger PDF"
                        >
                          <FilePdf size={18} />
                        </button>
                        {hasService && !cancelled && (
                          <button
                            onClick={() => navigate(`/interventions?invoice_id=${invoice.id}&client_id=${invoice.client_id}`)}
                            data-testid={`create-intervention-from-invoice-${invoice.id}`}
                            className="inline-flex items-center justify-center p-2 text-emerald-600 hover:bg-emerald-50 rounded-md mr-1"
                            title="Créer une intervention rattachée à cette facture"
                          >
                            <Toolbox size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => openSendDialog(invoice)}
                          data-testid={`send-email-${invoice.id}`}
                          className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-1"
                          title="Envoyer par email"
                        >
                          <PaperPlaneRight size={18} />
                        </button>
                        {!cancelled && (
                          <button
                            onClick={() => openCreditNoteDialog(invoice)}
                            data-testid={`credit-note-${invoice.id}`}
                            className="inline-flex items-center justify-center p-2 text-purple-600 hover:bg-purple-50 rounded-md mr-1"
                            title="Émettre un avoir"
                          >
                            <ArrowsCounterClockwise size={18} />
                          </button>
                        )}
                        {invoice.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancelInvoice(invoice.id)}
                            data-testid={`cancel-invoice-${invoice.id}`}
                            className="inline-flex items-center justify-center p-2 text-red-700 hover:bg-red-50 rounded-md"
                            title="Annuler la facture (crée un avoir)"
                          >
                            <Prohibit size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  {invoices.length === 0 && (
                    <tr>
                      <td colSpan="6" className="py-12 text-center text-slate-500">
                        Aucune facture trouvée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}

        {/* Create Invoice Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-invoice-dialog">
            <DialogHeader>
              <DialogTitle>{editingInvoice ? `Modifier la facture ${editingInvoice.invoice_number}` : 'Créer une facture'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateInvoice} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
                <Select
                  value={invoiceForm.client_id}
                  onValueChange={(value) => setInvoiceForm({ ...invoiceForm, client_id: value })}
                  required
                >
                  <SelectTrigger data-testid="invoice-client-select">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Add Items Section */}
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Ajouter des articles</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setItemMode('product')}
                      data-testid="item-mode-product-btn"
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors duration-200 ${
                        itemMode === 'product'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      Produit en stock
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemMode('custom')}
                      data-testid="item-mode-custom-btn"
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors duration-200 ${
                        itemMode === 'custom'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      Article personnalisé
                    </button>
                  </div>
                </div>
                
                {itemMode === 'product' ? (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-9">
                      <ArticleSelector
                        value={selectedProductKey}
                        onValueChange={setSelectedProductKey}
                        keys={keys}
                        itProducts={itProducts}
                        services={services}
                        printing={printing}
                        businessCards={businessCards}
                        binding={binding}
                        prestations={prestations}
                        testId="item-product-select"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qté"
                        value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                        data-testid="item-quantity-input"
                        min="1"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        onClick={addItemToInvoice}
                        data-testid="add-item-btn"
                        className="bg-blue-600 hover:bg-blue-500 text-white w-full"
                      >
                        <Plus size={20} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-2">
                      <Input
                        placeholder="Type"
                        value={currentItem.type}
                        onChange={(e) => setCurrentItem({ ...currentItem, type: e.target.value })}
                        data-testid="item-type-input"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        placeholder="Description"
                        value={currentItem.description}
                        onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                        data-testid="item-description-input"
                      />
                    </div>
                    <div className="col-span-1">
                      <Input
                        type="number"
                        placeholder="Qté"
                        value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                        data-testid="item-quantity-input"
                        min="1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Prix TTC"
                        value={currentItem.unit_price}
                        onChange={(e) => setCurrentItem({ ...currentItem, unit_price: e.target.value })}
                        data-testid="item-price-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <VatRateSelect
                        value={currentItem.vat_rate}
                        onChange={(v) => setCurrentItem({ ...currentItem, vat_rate: v })}
                        testId="item-vat-rate-select"
                      />
                    </div>
                    <div className="col-span-2">
                      <Button
                        type="button"
                        onClick={addItemToInvoice}
                        data-testid="add-item-btn"
                        className="bg-blue-600 hover:bg-blue-500 text-white w-full"
                      >
                        <Plus size={20} />
                      </Button>
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-3">
                  💡 Les produits en stock seront automatiquement décrémentés. Utilisez "Article personnalisé" pour les services (impression, reliure, etc.).
                </p>
              </div>

              {/* Items List */}
              {invoiceForm.items.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-xs uppercase text-slate-600">Description</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Qté</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">P.U. HT</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Remise %</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">P.U. HT remisé</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Total HT</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceForm.items.map((item, index) => {
                        const pct = Math.max(0, Math.min(100, parseFloat(item.discount_percent) || 0));
                        const unitAfter = (parseFloat(item.unit_price) || 0) * (1 - pct / 100);
                        return (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-2 px-3 text-slate-900">
                              <div className="text-slate-500 text-xs">{item.type}</div>
                              {item.description}
                            </td>
                            <td className="py-2 px-2 text-slate-900 text-right">{item.quantity}</td>
                            <td className="py-2 px-2 text-slate-900 text-right">{(parseFloat(item.unit_price) || 0).toFixed(2)} €</td>
                            <td className="py-2 px-2 text-right">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={pct}
                                onChange={(e) => updateItemDiscount(index, e.target.value)}
                                data-testid={`item-discount-percent-${index}`}
                                className="w-16 px-1 py-1 text-xs border border-slate-300 rounded text-right"
                              />
                            </td>
                            <td className="py-2 px-2 text-slate-700 text-right">{unitAfter.toFixed(2)} €</td>
                            <td className="py-2 px-2 text-slate-900 text-right font-medium">{item.total_price.toFixed(2)} €</td>
                            <td className="py-2 px-2 text-right">
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="text-red-600 hover:text-red-800"
                                data-testid={`remove-item-${index}`}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-col items-end gap-1" data-testid="invoice-totals-summary">
                    <div className="w-72 flex justify-between text-slate-700">
                      <span>Total HT</span>
                      <span data-testid="invoice-total-ht" className="font-medium">{totalHT.toFixed(2)} €</span>
                    </div>
                    <div className="w-72 flex justify-between text-slate-700">
                      <span>TVA {(displayVatRate * 100).toFixed(1).replace('.0', '')}%</span>
                      <span data-testid="invoice-total-vat" className="font-medium">{totalVAT.toFixed(2)} €</span>
                    </div>
                    <div className="w-72 flex justify-between border-t border-slate-300 pt-1 mt-1 text-slate-900 font-bold text-lg">
                      <span>Total TTC</span>
                      <span data-testid="invoice-total-ttc">{totalTTC.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mode de paiement</label>
                <Select
                  value={invoiceForm.payment_method}
                  onValueChange={(v) => setInvoiceForm({ ...invoiceForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="invoice-payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Espèces</SelectItem>
                    <SelectItem value="card">Carte bancaire</SelectItem>
                    <SelectItem value="transfer">Virement</SelectItem>
                    <SelectItem value="check">Chèque</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  💰 Un mouvement de caisse sera automatiquement créé si une session est ouverte
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Informations complémentaires</label>
                <textarea
                  value={invoiceForm.additional_info}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, additional_info: e.target.value })}
                  data-testid="invoice-additional-info-input"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Informations supplémentaires à afficher sur la facture..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <Input
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                  data-testid="invoice-notes-input"
                  placeholder="Notes supplémentaires..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  data-testid="cancel-invoice-btn"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-invoice-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white"
                >
                  {editingInvoice ? 'Mettre à jour' : 'Créer la facture'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Send Email Dialog */}
        <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <DialogContent data-testid="send-email-dialog">
            <DialogHeader>
              <DialogTitle>Envoyer la facture par email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Adresse email du destinataire
                </label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  data-testid="recipient-email-input"
                  placeholder="email@example.com"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowSendDialog(false)}
                  data-testid="cancel-send-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={sendEmail}
                  data-testid="confirm-send-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white"
                >
                  <PaperPlaneRight size={20} className="mr-2" />
                  Envoyer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Credit Note Dialog */}
        <Dialog open={showCreditNoteDialog} onOpenChange={setShowCreditNoteDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="credit-note-dialog">
            <DialogHeader>
              <DialogTitle>
                Émettre un avoir
                {creditNoteInvoice && <span className="text-sm font-normal text-slate-500 ml-2">(facture {creditNoteInvoice.invoice_number})</span>}
              </DialogTitle>
            </DialogHeader>
            {creditNoteInvoice && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                  Sélectionnez les articles à créditer et la quantité. Le stock des produits sera restauré et un mouvement de caisse négatif sera créé si une session est ouverte.
                </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        <th className="py-2 px-3 text-xs uppercase text-slate-600 w-10"></th>
                        <th className="text-left py-2 px-3 text-xs uppercase text-slate-600">Article</th>
                        <th className="text-right py-2 px-3 text-xs uppercase text-slate-600">Qté facture</th>
                        <th className="text-right py-2 px-3 text-xs uppercase text-slate-600">Déjà avoir</th>
                        <th className="text-right py-2 px-3 text-xs uppercase text-slate-600">Reste</th>
                        <th className="text-right py-2 px-3 text-xs uppercase text-slate-600">Qté à créditer</th>
                        <th className="text-right py-2 px-3 text-xs uppercase text-slate-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditNoteInvoice.items.map((item, idx) => {
                        const cf = creditNoteForm.items[idx] || { selected: false, quantity: 0 };
                        const credited = alreadyCredited[idx] || 0;
                        const remaining = item.quantity - credited;
                        const isAllCredited = remaining <= 0;
                        return (
                          <tr key={idx} className={`border-b border-slate-100 ${isAllCredited ? 'opacity-50' : ''}`}>
                            <td className="py-2 px-3 text-center">
                              <input
                                type="checkbox"
                                checked={cf.selected || false}
                                disabled={isAllCredited}
                                data-testid={`credit-item-${idx}-checkbox`}
                                onChange={(e) => {
                                  const updated = [...creditNoteForm.items];
                                  updated[idx] = {
                                    item_index: idx,
                                    selected: e.target.checked,
                                    quantity: e.target.checked ? remaining : 0,
                                  };
                                  setCreditNoteForm({ ...creditNoteForm, items: updated });
                                }}
                              />
                            </td>
                            <td className="py-2 px-3 text-sm">
                              <div className="font-medium text-slate-900">{item.description}</div>
                              <div className="text-xs text-slate-500">{item.type} · {item.unit_price.toFixed(2)} €</div>
                            </td>
                            <td className="py-2 px-3 text-sm text-right">{item.quantity}</td>
                            <td className="py-2 px-3 text-sm text-right text-orange-600">{credited}</td>
                            <td className="py-2 px-3 text-sm text-right font-medium">{remaining}</td>
                            <td className="py-2 px-3 text-right">
                              <Input
                                type="number"
                                step="1"
                                min="0"
                                max={remaining}
                                value={cf.quantity || ''}
                                disabled={!cf.selected || isAllCredited}
                                data-testid={`credit-item-${idx}-quantity`}
                                onChange={(e) => {
                                  const updated = [...creditNoteForm.items];
                                  updated[idx] = {
                                    item_index: idx,
                                    selected: cf.selected,
                                    quantity: Math.min(parseFloat(e.target.value) || 0, remaining),
                                  };
                                  setCreditNoteForm({ ...creditNoteForm, items: updated });
                                }}
                                className="w-24 text-right"
                              />
                            </td>
                            <td className="py-2 px-3 text-sm text-right font-medium text-red-600">
                              {cf.selected && cf.quantity > 0 ? `-${(cf.quantity * item.unit_price).toFixed(2)} €` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-red-50">
                        <td colSpan="6" className="py-3 px-3 text-right font-semibold text-slate-900">Total avoir :</td>
                        <td className="py-3 px-3 text-right font-bold text-lg text-red-600" data-testid="credit-note-total">
                          -{creditTotalAmount.toFixed(2)} €
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motif de l'avoir</label>
                  <textarea
                    rows={3}
                    value={creditNoteForm.reason}
                    onChange={(e) => setCreditNoteForm({ ...creditNoteForm, reason: e.target.value })}
                    data-testid="credit-note-reason-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Retour produit, erreur, geste commercial..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowCreditNoteDialog(false)}>Annuler</Button>
                  <Button
                    onClick={submitCreditNote}
                    data-testid="submit-credit-note-btn"
                    className="bg-red-600 hover:bg-red-500 text-white"
                  >
                    <ArrowsCounterClockwise size={18} className="mr-2" />
                    Émettre l'avoir
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Invoices;
